
import Vue from 'vue';
import organizationApi from "@/services/api.organizationSettings.ts";
import { Getter } from "vuex-class";
import { TenantSystemPrefs, ExpectedAlarmFailureGroupingOption } from "@/store/types";
import Component from "vue-class-component";
import EventOutcomeSelect from "@/components/form/EventOutcomeSelect.vue";
import { get } from "lodash";
import { FeaturesList } from "@/store/types";
import vSelect3 from "vselect3";
import { MaskOption } from '@/store/alarm-masking/types';
import { numberFormattingMixins } from '@/mixins';
import NotificationTemplateFormatOption, { notificationTemplateFormats } from "@/types/sv-data/notification-templates/NotificationTemplateFormatOption";
import NotificationDocumentTypeId from '@/types/sv-data/enums/NotificationDocumentTypeIds';

const { enforceWholeNumber } = numberFormattingMixins.methods;

@Component({
	components:{
		"event-outcome-select": EventOutcomeSelect,
		"v-select-3": vSelect3,
	}
})

export default class TenantSystemPrefsForm extends Vue {
	// Getters
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter getNotificationTemplateDefaultFormat: number;
	@Getter getFeature: (featuresList: string[]) => boolean;

	public get tenantDefaultMaskingValueEnabled(): boolean {
		return get(this.featuresList, ["TenantSettings", "DefaultMasking"], false);
	}

	public get tenantDefaultParkingValueEnabled(): boolean {
		return get(this.featuresList, ["TenantSettings", "DefaultParking"], false);
	}

	private get isAlarmAgeWarningEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "AlarmAgeWarning"], false);
	}

	public get tenantDefaultMaskActionEnabled(): boolean {
		return get(this.featuresList, ["TenantSettings", "ChangeMaskAction"], false);
	}

	public get slaEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "SLAs"], false);
	}

	public get expectedAlarmFailureEnabled(): boolean {
		return get(this.featuresList, ["Devices", "AdvancedSetup", "ExpectedAlarmFailures"]);
	}

	public get applianceFailoverEnabled(): boolean {
		return get(this.featuresList, ["Devices", "AdvancedSetup", "ApplianceFailover"]);
	}

	private get isNotificationTemplatesEmbeddedHtmlEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "EmbeddedHtml"]);
	}

	public get isLockArchivedEntriesEnabled(): boolean {
		return this.getFeature(["ActivityLog", "LockArchivedEntries"]);
	}

	public get isInteractiveConsoleEnabled(): boolean {
		return this.getFeature(["InteractiveConsole"]);
	}

	public get isReplyToEmailAddressEnabled(): boolean {
		return this.getFeature(["TenantSettings", "ReplyToEmailAddress"]);
	}

	private tenantSystemPrefs: TenantSystemPrefs;

	private tenantSettings: TenantSystemPrefs = {
		defaultMaskValue: 10,
		defaultParkValue: 10,
		alarmAgeWarningSeconds: null,
		defaultMaskAction: false,
		maxResponseSlaDuration: 60,
		responseSlaNotifyAtSecondsRemaining: 30,
		expectedAlarmFailureGrouping: null,
		applianceFailoverNotificationEmails: null,
		notificationTemplateDefaultFormat: NotificationDocumentTypeId.Png,
		graphColors: null,
		archiveActivityLogAt: 24,
		interactiveConsoleURL: null,
		replyToEmailAddress: null,
	};

	private newDefaultMaskValue: number = 10;
	private newDefaultParkValue: number = 10;
	private newAlarmAgeWarningSeconds: number | null = null;
	private newDefaultMaskAction: boolean = false;
	private maxIntPrefValeInteger: number = 2147483647;
	private minIntPrefValeInteger: number = 1;
	private maxMaskDuration: number = 52560;
	private maskOptions: MaskOption[] = [{ title: `Ignore`, value: false }, { title: `AutoHandle`, value: true }];
	private maxParkValueInMinutes: number = 1440; // 24 hours
	private newExpectedAlarmFailureGrouping: number | null = null;
	private newApplianceFailoverNotificationEmails: string[] = [];
	private newinteractiveConsoleURL: string | null = null;
	private newReplyToEmailAddress: string | null = null;
	private emailsAsCommaSeparatedString: string | null = null;
	private expectedAlarmFailureGroupingOption: ExpectedAlarmFailureGroupingOption[] = [
		{
			title: "Default",
			value: null
		},
		{
			title: "Device",
			value: 1
		},
		{
			title: "Area",
			value: 2
		},
		{
			title: "Tenant",
			value: 3
		}
	]

	private notificationTemplateFormats: NotificationTemplateFormatOption[] = notificationTemplateFormats;
	private newDefaultNotificationTemplateFormat: NotificationTemplateFormatOption = { id: NotificationDocumentTypeId.Png, label: "Embedded" };

	private newMaxResponseSlaDuration: number = 2147483647;
	private newResponseSlaNotifyAtSecondsRemaining: number = 60;
	private minActivityLogExpirationTime: number = 0;
	private newActivityLogExpirationTime: number = 24; // Default to 24 hours

	private validEmailRegex: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;

	private async beforeMount(): Promise<void> {
		try {
			this.tenantSystemPrefs = await organizationApi.retrieveTenantSystemPrefs(true);
		} catch (e) {
			console.log("Failed to get tenant system prefs");
		}

		if (!this.tenantSettings) {
			this.newDefaultMaskValue = null;
			this.newDefaultParkValue = null;
			this.newAlarmAgeWarningSeconds = null;
			this.newDefaultMaskAction = false;
			this.newExpectedAlarmFailureGrouping = null;
			const defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.id === this.getNotificationTemplateDefaultFormat);
			this.newDefaultNotificationTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
			this.newinteractiveConsoleURL = null;
			this.newReplyToEmailAddress = null;
			return;
		}

		this.tenantSettings.defaultMaskValue = this.tenantSystemPrefs?.defaultMaskValue;
		this.tenantSettings.defaultParkValue = this.tenantSystemPrefs?.defaultParkValue;
		this.tenantSettings.alarmAgeWarningSeconds = this.tenantSystemPrefs?.alarmAgeWarningSeconds;
		this.tenantSettings.defaultMaskAction = this.tenantSystemPrefs?.defaultMaskAction;
		this.tenantSettings.maxResponseSlaDuration = this.tenantSystemPrefs?.maxResponseSlaDuration;
		this.tenantSettings.responseSlaNotifyAtSecondsRemaining = this.tenantSystemPrefs?.responseSlaNotifyAtSecondsRemaining;
		this.tenantSettings.expectedAlarmFailureGrouping = this.tenantSystemPrefs?.expectedAlarmFailureGrouping;
		this.tenantSettings.applianceFailoverNotificationEmails = this.tenantSystemPrefs?.applianceFailoverNotificationEmails;
		this.tenantSettings.notificationTemplateDefaultFormat = this.tenantSystemPrefs?.notificationTemplateDefaultFormat;
		this.tenantSettings.archiveActivityLogAt = this.tenantSystemPrefs?.archiveActivityLogAt;
		this.tenantSettings.interactiveConsoleURL = this.tenantSystemPrefs?.interactiveConsoleURL;
		this.tenantSettings.replyToEmailAddress = this.tenantSystemPrefs?.replyToEmailAddress;

		this.newDefaultMaskValue = this.tenantSettings?.defaultMaskValue;
		this.newDefaultParkValue = this.tenantSettings?.defaultParkValue;
		this.newAlarmAgeWarningSeconds = this.tenantSettings?.alarmAgeWarningSeconds;
		this.newDefaultMaskAction = this.tenantSettings?.defaultMaskAction;
		this.newMaxResponseSlaDuration = this.tenantSettings?.maxResponseSlaDuration;
		this.newResponseSlaNotifyAtSecondsRemaining = this.tenantSettings?.responseSlaNotifyAtSecondsRemaining;
		this.newExpectedAlarmFailureGrouping = this.tenantSettings?.expectedAlarmFailureGrouping;
		this.emailsAsCommaSeparatedString = this.tenantSettings?.applianceFailoverNotificationEmails;
		this.newApplianceFailoverNotificationEmails = this.tenantSettings?.applianceFailoverNotificationEmails ? this.tenantSettings.applianceFailoverNotificationEmails.split(',').map(e => e.trim()) : null;
		this.newDefaultNotificationTemplateFormat = this.notificationTemplateFormats.find(f => f.id === this.tenantSettings?.notificationTemplateDefaultFormat);
		this.newActivityLogExpirationTime = this.tenantSettings?.archiveActivityLogAt;
		this.newinteractiveConsoleURL = this.tenantSettings?.interactiveConsoleURL;
		this.newReplyToEmailAddress = this.tenantSettings?.replyToEmailAddress;
}

	private get haveSettingsChanged(): boolean {
		return this.tenantSettings?.defaultMaskValue !== this.newDefaultMaskValue ||
			this.tenantSettings?.defaultParkValue !== this.newDefaultParkValue ||
			this.tenantSettings?.alarmAgeWarningSeconds !== this.newAlarmAgeWarningSeconds ||
			this.tenantSettings?.defaultMaskAction !== this.newDefaultMaskAction ||
			this.tenantSettings?.maxResponseSlaDuration !== this.newMaxResponseSlaDuration ||
			this.tenantSettings?.responseSlaNotifyAtSecondsRemaining !== this.newResponseSlaNotifyAtSecondsRemaining ||
			this.tenantSettings?.expectedAlarmFailureGrouping !== this.newExpectedAlarmFailureGrouping ||
			this.emailsAsCommaSeparatedString !== this.tenantSettings?.applianceFailoverNotificationEmails ||
			this.tenantSettings?.notificationTemplateDefaultFormat !== this.newDefaultNotificationTemplateFormat.id ||
			this.tenantSettings?.archiveActivityLogAt !== this.newActivityLogExpirationTime ||
			this.tenantSettings?.interactiveConsoleURL !== this.newinteractiveConsoleURL ||
			this.tenantSettings?.replyToEmailAddress !== this.newReplyToEmailAddress;
	}

	private async resetChanges(): Promise<void> {
		this.newDefaultMaskValue = this.tenantSettings.defaultMaskValue;
		this.newDefaultParkValue = this.tenantSettings.defaultParkValue;
		this.newAlarmAgeWarningSeconds = this.tenantSettings.alarmAgeWarningSeconds;
		this.newDefaultMaskAction = this.tenantSettings.defaultMaskAction;
		this.newMaxResponseSlaDuration = this.tenantSettings.maxResponseSlaDuration;
		this.newResponseSlaNotifyAtSecondsRemaining = this.tenantSettings.responseSlaNotifyAtSecondsRemaining;
		this.newExpectedAlarmFailureGrouping = this.tenantSettings.expectedAlarmFailureGrouping;
		this.newApplianceFailoverNotificationEmails = this.tenantSettings.applianceFailoverNotificationEmails ? this.tenantSettings.applianceFailoverNotificationEmails.split(',').map(e => e.trim()) : null;
		this.emailsAsCommaSeparatedString = this.tenantSettings.applianceFailoverNotificationEmails;
		const defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.id === this.getNotificationTemplateDefaultFormat);
		this.newDefaultNotificationTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
		this.newActivityLogExpirationTime = this.tenantSettings.archiveActivityLogAt;
		this.newinteractiveConsoleURL = this.tenantSettings.interactiveConsoleURL;
		this.newReplyToEmailAddress = this.tenantSettings.replyToEmailAddress;
	}

	private get parkState(): boolean{
		if(this.newDefaultParkValue && this.newDefaultParkValue < this.minIntPrefValeInteger){
			return false;
		}

		if(!this.tenantSettings.defaultParkValue)
		{
			return true;
		}

		return this.newDefaultParkValue <= this.maxParkValueInMinutes;
	}

	private get maskState(): boolean{
		if(this.newDefaultMaskValue && this.newDefaultMaskValue < this.minIntPrefValeInteger){
			return false;
		}

		if(!this.tenantSettings.defaultMaskValue)
		{
			return true;
		}

		return this.newDefaultMaskValue <= this.maxMaskDuration;
	}

	private get alarmAgeWarningState(): boolean {
		if (!!this.newAlarmAgeWarningSeconds || this.newAlarmAgeWarningSeconds === 0) {
			return this.newAlarmAgeWarningSeconds >= this.minIntPrefValeInteger && this.newAlarmAgeWarningSeconds <= this.maxIntPrefValeInteger;
		}

		return true;
    }

	private get maxSlaValueValidator(): boolean {
		if (!!this.newMaxResponseSlaDuration || this.newMaxResponseSlaDuration === 0) {
			return this.newMaxResponseSlaDuration >= this.minIntPrefValeInteger && this.newMaxResponseSlaDuration <= this.maxIntPrefValeInteger;
		}

		return true;
    }

	private get slaNotifyThresholdValidator(): boolean {
		if (!!this.newResponseSlaNotifyAtSecondsRemaining || this.newResponseSlaNotifyAtSecondsRemaining === 0) {
			return this.newResponseSlaNotifyAtSecondsRemaining >= this.minIntPrefValeInteger && this.newResponseSlaNotifyAtSecondsRemaining <= this.maxIntPrefValeInteger;
		}

		return true;
    }

	private invalidEmailErrorMessage(): string {
		if (this.newApplianceFailoverNotificationEmails && Array.isArray(this.newApplianceFailoverNotificationEmails)) {
			const invalidEmails = this.newApplianceFailoverNotificationEmails.filter(email => !this.validEmailRegex.test(email));

			return `Please enter a valid email address. Invalid email(s): ${invalidEmails.join(", ")}`
		}
	}

	private get isEmailValid(): boolean {
		if (this.newApplianceFailoverNotificationEmails && Array.isArray(this.newApplianceFailoverNotificationEmails)) {
			return this.newApplianceFailoverNotificationEmails.every(email => this.validEmailRegex.test(email));
		}

		return true;
	}

	private get isReplyToEmailValid(): boolean {
		if (!this.newReplyToEmailAddress || this.newReplyToEmailAddress === "") {
			return true;
		}

		return this.validEmailRegex.test(this.newReplyToEmailAddress);
	}

	private get emailsCommaSeparated(): string {
		if (!this.newApplianceFailoverNotificationEmails || !Array.isArray(this.newApplianceFailoverNotificationEmails) || this.newApplianceFailoverNotificationEmails.length === 0) {
			return null;
		}

		return this.newApplianceFailoverNotificationEmails.join(",");
	}

	private validateEmails(): void {
		this.emailsAsCommaSeparatedString = this.emailsCommaSeparated;
	}

	private tenantSettingsFormatter(tenantSettingValue: number): number {
		return enforceWholeNumber(tenantSettingValue);
	}

	private maxSlaValueFormatter(maxSlaValue: number): number {
		return enforceWholeNumber(maxSlaValue);
	}

	private get activityLogExpirationTimeFormatter(): boolean {
		if (this.newActivityLogExpirationTime) {
			return this.newActivityLogExpirationTime >= this.minActivityLogExpirationTime && this.newActivityLogExpirationTime <= this.maxIntPrefValeInteger;
		}

		return true;
	}

	private async confirmUpdateTenantSettings(): Promise<void> {
		const newTenantSettings: TenantSystemPrefs = {
			defaultMaskValue: this.newDefaultMaskValue,
			defaultParkValue: this.newDefaultParkValue,
			alarmAgeWarningSeconds: this.newAlarmAgeWarningSeconds,
			defaultMaskAction: this.newDefaultMaskAction,
			maxResponseSlaDuration: this.newMaxResponseSlaDuration,
			responseSlaNotifyAtSecondsRemaining: this.newResponseSlaNotifyAtSecondsRemaining,
			expectedAlarmFailureGrouping: this.newExpectedAlarmFailureGrouping,
			applianceFailoverNotificationEmails: this.emailsAsCommaSeparatedString === "" ? null : this.emailsAsCommaSeparatedString,
			notificationTemplateDefaultFormat: this.newDefaultNotificationTemplateFormat.id,
			graphColors: this.tenantSystemPrefs?.graphColors,
			archiveActivityLogAt: this.newActivityLogExpirationTime,
			interactiveConsoleURL: this.newinteractiveConsoleURL,
			replyToEmailAddress: this.newReplyToEmailAddress,
		};

		try {
			await organizationApi.createOrUpdateTenantSystemPrefs(newTenantSettings);
		} catch (e) {
			console.log("Failed to add or update tenant system prefs");
		}

		this.tenantSettings.defaultParkValue = newTenantSettings.defaultParkValue;
		this.tenantSettings.defaultMaskValue = newTenantSettings.defaultMaskValue;
		this.tenantSettings.alarmAgeWarningSeconds = newTenantSettings.alarmAgeWarningSeconds;
		this.tenantSettings.defaultMaskAction = newTenantSettings.defaultMaskAction;
		this.tenantSettings.maxResponseSlaDuration = newTenantSettings.maxResponseSlaDuration;
		this.tenantSettings.responseSlaNotifyAtSecondsRemaining = newTenantSettings.responseSlaNotifyAtSecondsRemaining;
		this.tenantSettings.expectedAlarmFailureGrouping = newTenantSettings.expectedAlarmFailureGrouping;
		this.tenantSettings.applianceFailoverNotificationEmails = newTenantSettings.applianceFailoverNotificationEmails;
		this.tenantSettings.archiveActivityLogAt = newTenantSettings.archiveActivityLogAt;
		this.tenantSettings.replyToEmailAddress = newTenantSettings.replyToEmailAddress;

		this.$notify({
			type: "success",
			title: "Tenant settings updated"
		});
	}

	private get UpdateButtonDisabled(): boolean {
		return !this.haveSettingsChanged
		|| !this.maskState
		|| !this.parkState
		|| !this.alarmAgeWarningState
		|| !this.maxSlaValueValidator
		|| !this.slaNotifyThresholdValidator
		|| !this.isEmailValid
		|| !this.activityLogExpirationTimeFormatter
		|| !this.isReplyToEmailValid;
	}
}
